/**
 * Componente para crear un nuevo ticket de soporte
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 3
 *            sistema-tickets-despliegue.md Sección 8.1
 */

import React, { useState, useContext, useEffect } from 'react';
import { useTenantNavigation } from '../../hooks/soporte/useTenantNavigation';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import axios from 'axios';
import { ErpContext } from '../../context/ErpContext';
import { SoporteContext } from '../../context/SoporteContext';
import { validarCrearTicket } from '../../utils/soporte/soporteValidations';
import { formatFileSize } from '../../utils/soporte/soporteHelpers';
import AdjuntoUpload from './shared/AdjuntoUpload';
import dayjs from 'dayjs';

const CrearTicketComponent = () => {
  const navigate = useTenantNavigation();
  const { serverURL } = useContext(ErpContext);
  const { categorias, prioridades, config, loading: contextLoading, error: contextError } = useContext(SoporteContext);
  console.log('🔍 [CrearTicket] Config:', config);
  // Debug: Log para verificar datos del contexto
  useEffect(() => {
    console.log('🔍 [CrearTicket] Context Loading:', contextLoading);
    console.log('🔍 [CrearTicket] Prioridades:', prioridades);
    console.log('🔍 [CrearTicket] Categorías:', categorias);
    console.log('🔍 [CrearTicket] Context Error:', contextError);
  }, [contextLoading, prioridades, categorias, contextError]);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    tipo: 'incidencia',
    titulo: '',
    descripcion: '',
    categoriaId: '',
    prioridadId: ''
  });
  
  // Estado de adjuntos
  const [adjuntos, setAdjuntos] = useState([]);
  
  // Estado de validación
  const [errors, setErrors] = useState({});
  
  // Estado de envío
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const normalizarTexto = (texto = '') =>
    texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const prioridadesActivasOrdenadas = (prioridades || [])
    .filter(p => p.activa && !p.eliminado)
    .sort((a, b) => (a.orden || 0) - (b.orden || 0));

  const prioridadBaja = prioridadesActivasOrdenadas.find(
    (prioridad) => ['bajo', 'baja'].includes(normalizarTexto(prioridad.nombre))
  );
  
  // Prioridad seleccionada para preview de SLA
  const prioridadSeleccionada = prioridades.find(
    (p) => String(p.id) === String(formData.prioridadId)
  );

  // Si el tipo es solicitud, fijar automáticamente prioridad baja
  useEffect(() => {
    if (formData.tipo === 'solicitud' && prioridadBaja) {
      setFormData((prev) => ({
        ...prev,
        prioridadId: String(prioridadBaja.id)
      }));
    }
  }, [formData.tipo, prioridadBaja]);
  
  /**
   * Manejar cambio en campos del formulario
   */
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo al cambiar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  
  /**
   * Manejar cambio en adjuntos
   */
  const handleAdjuntosChange = (nuevosAdjuntos) => {
    setAdjuntos(nuevosAdjuntos);
  };
  
  /**
   * Validar formulario antes de enviar
   */
  const validarFormulario = () => {
    const validationErrors = validarCrearTicket(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };
  
  /**
   * Manejar envío del formulario
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validar
    if (!validarFormulario()) {
      setError('Por favor, complete todos los campos requeridos correctamente');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Preparar datos para enviar como JSON con archivos en base64
      const datosParaEnviar = {
        tipo: formData.tipo,
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion.trim(),
        categoriaId: formData.categoriaId,
        prioridadId: formData.prioridadId,
        adjuntos: adjuntos.map(adjunto => ({
          name: adjunto.name,
          type: adjunto.type,
          size: adjunto.size,
          data: adjunto.data // Ya está en base64 desde AdjuntoUpload.js
        }))
      };
      
      // Enviar request
      const response = await axios.post(
        `${serverURL}/soporteAPI/tickets`,
        datosParaEnviar,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Éxito: mostrar mensaje y navegar
      if (response.data && response.data.ticket && response.data.ticket.id) {
        const ticketNumero = response.data.ticket.numero || response.data.ticket.id;
        setSuccess(`Ticket ${ticketNumero} creado exitosamente. Redirigiendo...`);
        setError(''); // Limpiar errores previos
        
        // Esperar un momento para que el usuario vea el mensaje antes de navegar
        setTimeout(() => {
          // Guardar origen de navegación: 'crear' para tickets creados
          sessionStorage.setItem('ticket_navegacion_origen', 'crear');
          navigate(`/soporte/ticket/${response.data.ticket.id}`);
        }, 1500);
      } else {
        // Si no hay ID, mostrar mensaje genérico y navegar a lista
        setSuccess('Ticket creado exitosamente. Redirigiendo...');
        setError('');
        setTimeout(() => {
          navigate('/soporte/tickets');
        }, 1500);
      }
    } catch (err) {
      console.error('Error creando ticket:', err);
      setError(
        err.response?.data?.error || 
        err.response?.data?.message || 
        'Error al crear el ticket. Por favor, intente nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Mostrar loading si el contexto aún está cargando
  if (contextLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" sx={{caretColor: 'transparent'}} gutterBottom>
        Crear Nuevo Ticket
      </Typography>
      <Typography variant="body2" sx={{caretColor: 'transparent'}} color="text.secondary" mb={3}>
        Complete el formulario para crear un nuevo ticket de soporte
      </Typography>
      
      {/* Mensaje de éxito */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Error general */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Formulario */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Tipo de Ticket */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.tipo}>
                <InputLabel>Tipo de Ticket</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={handleChange('tipo')}
                  label="Tipo de Ticket"
                >
                  <MenuItem value="incidencia">Incidencia (Bug, Error, Falla)</MenuItem>
                  <MenuItem value="solicitud">Solicitud (Consulta, Ayuda)</MenuItem>
                </Select>
                {errors.tipo && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.tipo}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Prioridad */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                required
                error={!!errors.prioridadId}
                disabled={contextLoading || formData.tipo === 'solicitud'}
              >
                <InputLabel>Prioridad</InputLabel>
                <Select
                  value={formData.prioridadId}
                  onChange={handleChange('prioridadId')}
                  label="Prioridad"
                >
                  {contextLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Cargando prioridades...
                    </MenuItem>
                  ) : formData.tipo === 'solicitud' ? (
                    prioridadBaja ? (
                      <MenuItem key={prioridadBaja.id} value={prioridadBaja.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: prioridadBaja.color || '#757575'
                            }}
                          />
                          {prioridadBaja.nombre}
                        </Box>
                      </MenuItem>
                    ) : (
                      <MenuItem disabled>
                        No hay prioridad "bajo" disponible
                      </MenuItem>
                    )
                  ) : prioridadesActivasOrdenadas.length > 0 ? (
                    prioridadesActivasOrdenadas.map(prioridad => (
                        <MenuItem key={prioridad.id} value={prioridad.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: prioridad.color || '#757575'
                              }}
                            />
                            {prioridad.nombre}
                          </Box>
                        </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      No hay prioridades disponibles
                    </MenuItem>
                  )}
                </Select>
                {errors.prioridadId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.prioridadId}
                  </Typography>
                )}
                {contextError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    Error al cargar prioridades: {contextError}
                  </Typography>
                )}
                {prioridadSeleccionada && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, ml: 1.5, display: 'block' }}
                  >
                    Tiempo de respuesta {prioridadSeleccionada.firstResponseHours ?? 0}hs
                  </Typography>
                )}
                {formData.tipo === 'solicitud' && prioridadBaja && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    Para solicitudes la prioridad es fija: {prioridadBaja.nombre}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Título */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Título"
                value={formData.titulo}
                onChange={handleChange('titulo')}
                error={!!errors.titulo}
                helperText={errors.titulo || 'Mínimo 10 caracteres, máximo 150'}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>
            
            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Descripción"
                value={formData.descripcion}
                onChange={handleChange('descripcion')}
                error={!!errors.descripcion}
                helperText={errors.descripcion || 'Mínimo 20 caracteres, máximo 1,000. Describa el problema o solicitud con detalle.'}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
            
            {/* Categoría */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.categoriaId}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={formData.categoriaId}
                  onChange={handleChange('categoriaId')}
                  label="Categoría"
                >
                  {categorias
                    .filter(c => c.activa && !c.eliminado)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                    .map(categoria => (
                      <MenuItem key={categoria.id} value={categoria.id}>
                        {categoria.nombre}
                        {categoria.modulo && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({categoria.modulo})
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                </Select>
                {errors.categoriaId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.categoriaId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Preview de SLA */}
            {/* {prioridadSeleccionada && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      SLA Estimado
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Primera Respuesta:
                        </Typography>
                        <Chip
                          label={`${prioridadSeleccionada.firstResponseHours || prioridadSeleccionada.firstResponseHours || 0} horas`}
                          size="small"
                          color="primary"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Resolución:
                        </Typography>
                        <Chip
                          label={`${prioridadSeleccionada.resolutionHours || prioridadSeleccionada.resolutionHours || 0} horas`}
                          size="small"
                          color="secondary"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )} */}
            
            {/* Adjuntos */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Archivos Adjuntos (Opcional)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Máximo {config?.maxAttachmentsPerMessage || 5} archivos, 
                {config?.maxAttachmentSizeMb || 50}MB por archivo
              </Typography>
              <AdjuntoUpload
                adjuntos={adjuntos}
                onChange={handleAdjuntosChange}
                maxFiles={config?.maxAttachmentsPerMessage || 5}
                maxSizeMB={config?.maxAttachmentSizeMb || 50}
              />
            </Grid>
            
            {/* Botones */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/soporte/tickets')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Creando...' : 'Crear Ticket'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CrearTicketComponent;

